// client/src/pages/wholesaler/WholesalerSubscription.js
// Comprehensive subscription management dashboard for wholesalers
// Shows billing history, usage stats, and subscription details

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import { wholesalerAPI } from '../../services/api';
import BillingHistory from '../../components/wholesaler/BillingHistory';
import UsageChart from '../../components/wholesaler/UsageChart';
import TierUsageCard from '../../components/wholesaler/TierUsageCard';

const WholesalerSubscription = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['wholesaler', 'common']);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usageStats, setUsageStats] = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);
  const [currentMonthSummary, setCurrentMonthSummary] = useState(null);
  const [projectedOrders, setProjectedOrders] = useState(100);
  const [projectedCosts, setProjectedCosts] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // overview, history, projections

  useEffect(() => {
    fetchSubscriptionData();
  }, []);

  useEffect(() => {
    if (projectedOrders > 0) {
      fetchProjectedCosts();
    }
  }, [projectedOrders]);

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch usage stats
      const statsRes = await wholesalerAPI.getUsageStats();
      setUsageStats(statsRes.data.stats);

      // Fetch billing history (last 6 months)
      const historyRes = await wholesalerAPI.getBillingHistory(6);
      setBillingHistory(historyRes.data.transactions);

      // Fetch current month summary
      const now = new Date();
      const summaryRes = await wholesalerAPI.getMonthlySummary(
        now.getFullYear(),
        now.getMonth() + 1
      );
      setCurrentMonthSummary(summaryRes.data.summary);

    } catch (err) {
      console.error('Failed to fetch subscription data:', err);
      setError(err.response?.data?.message || 'Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectedCosts = async () => {
    try {
      const res = await wholesalerAPI.getProjectedCost(projectedOrders);
      setProjectedCosts(res.data);
    } catch (err) {
      console.error('Failed to fetch projected costs:', err);
    }
  };

  const getTierDisplayName = (tier) => {
    if (!tier) return 'N/A';
    return t(`subscription.tiers.${tier}`, tier.charAt(0).toUpperCase() + tier.slice(1));
  };

  const getTierBadgeColor = (tier) => {
    if (!tier) return 'bg-gray-100 text-gray-800 border-gray-300';
    const colors = {
      starter: 'bg-blue-600 text-white border-blue-700',
      professional: 'bg-purple-600 text-white border-purple-700',
      enterprise: 'bg-amber-600 text-white border-amber-700'
    };
    return colors[tier] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getTierCardBackground = (tier) => {
    if (!tier) return 'bg-white';
    const backgrounds = {
      starter: 'bg-blue-100',
      professional: 'bg-purple-100',
      enterprise: 'bg-amber-100'
    };
    return backgrounds[tier] || 'bg-white';
  };

  const formatCurrency = (amount) => {
    return `€${parseFloat(amount).toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex justify-center items-center h-64">
          <div className="text-xl text-gray-600">
            {t('subscription.loading', 'Loading subscription data...')}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-8 pt-20 lg:pt-8">
          <div className="max-w-2xl mx-auto">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <div className="text-red-600 text-5xl mb-4">⚠️</div>
              <h2 className="text-xl font-semibold text-red-800 mb-2">
                {t('subscription.dashboard.errorTitle', 'Unable to Load Subscription Data')}
              </h2>
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={fetchSubscriptionData}
                className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                {t('subscription.dashboard.retry', 'Try Again')}
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Show empty state if no usage stats
  if (!usageStats || !usageStats.currentTier) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-8 pt-20 lg:pt-8">
          <div className="max-w-2xl mx-auto">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
              <div className="text-yellow-600 text-5xl mb-4">📊</div>
              <h2 className="text-xl font-semibold text-yellow-800 mb-2">
                {t('subscription.dashboard.noDataTitle', 'Subscription Not Configured')}
              </h2>
              <p className="text-yellow-700 mb-4">
                {t('subscription.dashboard.noDataMessage', 'Your subscription plan needs to be set up. Please contact support.')}
              </p>
              <Link
                to="/wholesaler/pricing"
                className="inline-block bg-yellow-600 text-white px-6 py-2 rounded-lg hover:bg-yellow-700 transition-colors"
              >
                {t('subscription.dashboard.viewPlans', 'View Plans')}
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="container mx-auto px-4 py-8 pt-20 lg:pt-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            {t('subscription.dashboard.title', 'Subscription Dashboard')}
          </h1>
          <p className="text-gray-600">
            {t('subscription.dashboard.subtitle', 'Manage your subscription and view billing details')}
          </p>
        </div>

        {/* Current Plan Overview */}
        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          {/* Tier Info Card */}
          <div className={`lg:col-span-2 rounded-lg shadow-md p-6 ${getTierCardBackground(usageStats?.currentTier)}`}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-800 mb-1">
                  {t('subscription.dashboard.currentPlan', 'Current Plan')}
                </h2>
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium border ${getTierBadgeColor(usageStats?.currentTier)}`}>
                  {getTierDisplayName(usageStats?.currentTier)}
                </span>
              </div>
              <Link
                to="/wholesaler/pricing"
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                {t('subscription.dashboard.viewPlans', 'View Plans')}
              </Link>
            </div>

            {/* Stats Grid */}
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-600 mb-1">
                  {t('subscription.dashboard.productUsage', 'Product Usage')}
                </div>
                <div className="text-2xl font-bold text-gray-800">
                  {usageStats?.currentProducts || 0}
                  {usageStats?.productLimit !== -1 && ` / ${usageStats?.productLimit}`}
                </div>
                <div className="text-xs text-gray-500">
                  {usageStats?.productLimit === -1 
                    ? t('subscription.unlimited', 'Unlimited')
                    : t('subscription.products', 'products')
                  }
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-600 mb-1">
                  {t('subscription.dashboard.ordersThisMonth', 'Orders This Month')}
                </div>
                <div className="text-2xl font-bold text-gray-800">
                  {usageStats?.ordersThisMonth || 0}
                </div>
                <div className="text-xs text-gray-500">
                  {t('subscription.dashboard.ordersReceived', 'orders received')}
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-600 mb-1">
                  {t('subscription.dashboard.estimatedCost', 'Estimated Cost')}
                </div>
                <div className="text-2xl font-bold text-gray-800">
                  {formatCurrency(usageStats?.currentMonthCost?.total || 0)}
                </div>
                <div className="text-xs text-gray-500">
                  {t('subscription.dashboard.thisMonth', 'this month')}
                </div>
              </div>
            </div>

            {/* Cost Breakdown */}
            {usageStats?.currentMonthCost && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  {t('subscription.dashboard.currentMonthBreakdown', 'Current Month Breakdown')}
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      {t('subscription.dashboard.subscriptionFee', 'Subscription Fee')}
                    </span>
                    <span className="font-medium">{formatCurrency(usageStats.currentMonthCost.subscription)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      {t('subscription.dashboard.orderFees', 'Order Fees')} ({usageStats.ordersThisMonth} × €{usageStats.currentMonthCost.perOrderFee})
                    </span>
                    <span className="font-medium">{formatCurrency(usageStats.currentMonthCost.orderFees)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-gray-200 font-semibold">
                    <span className="text-gray-800">
                      {t('subscription.dashboard.total', 'Total')}
                    </span>
                    <span className="text-gray-900">{formatCurrency(usageStats.currentMonthCost.total)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div>
            <TierUsageCard />
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-6 py-3 text-sm font-medium ${
                  activeTab === 'overview'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t('subscription.dashboard.tabs.overview', 'Overview')}
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`px-6 py-3 text-sm font-medium ${
                  activeTab === 'history'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t('subscription.dashboard.tabs.billingHistory', 'Billing History')}
              </button>
              <button
                onClick={() => setActiveTab('projections')}
                className={`px-6 py-3 text-sm font-medium ${
                  activeTab === 'projections'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t('subscription.dashboard.tabs.costProjections', 'Cost Projections')}
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'overview' && usageStats && (
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  {t('subscription.dashboard.usageTrends', 'Usage Trends (Last 6 Months)')}
                </h3>
                <UsageChart data={usageStats.last6Months} />
              </div>
            )}

            {activeTab === 'history' && (
              <div>
                <BillingHistory transactions={billingHistory} />
              </div>
            )}

            {activeTab === 'projections' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  {t('subscription.dashboard.costCalculator', 'Monthly Cost Calculator')}
                </h3>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('subscription.dashboard.estimatedOrders', 'Estimated orders per month:')}
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={projectedOrders}
                    onChange={(e) => setProjectedOrders(parseInt(e.target.value) || 0)}
                    className="w-full md:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-sm text-gray-600 mt-2">
                    {t('subscription.dashboard.adjustToCompare', 'Adjust to compare costs across different tiers')}
                  </p>
                </div>

                {projectedCosts && (
                  <div className="grid md:grid-cols-3 gap-4">
                    {Object.entries(projectedCosts.allTiersCosts).map(([tier, cost]) => {
                      const isCurrentTier = tier === projectedCosts.currentTier;
                      return (
                        <div
                          key={tier}
                          className={`border-2 rounded-lg p-4 ${
                            isCurrentTier 
                              ? 'border-blue-500 bg-blue-50' 
                              : 'border-gray-200 bg-white'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-semibold text-gray-800">
                              {getTierDisplayName(tier)}
                            </h4>
                            {isCurrentTier && (
                              <span className="text-xs bg-blue-600 text-white px-2 py-1 rounded">
                                {t('subscription.dashboard.current', 'Current')}
                              </span>
                            )}
                          </div>
                          <div className="space-y-1 text-sm mb-3">
                            <div className="flex justify-between">
                              <span className="text-gray-600">
                                {t('subscription.dashboard.subscription', 'Subscription')}
                              </span>
                              <span>{formatCurrency(cost.subscription)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">
                                {t('subscription.dashboard.orderFeesShort', 'Order Fees')}
                              </span>
                              <span>{formatCurrency(cost.orderFees)}</span>
                            </div>
                            <div className="flex justify-between pt-1 border-t border-gray-300 font-semibold">
                              <span>{t('subscription.dashboard.total', 'Total')}</span>
                              <span className="text-lg">{formatCurrency(cost.total)}</span>
                            </div>
                          </div>
                          <div className="text-xs text-gray-500">
                            €{cost.perOrderFee} {t('subscription.dashboard.perOrder', 'per order')}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default WholesalerSubscription;